/**
 * @file SendResetMailPasswordForm.tsx
 * @description
 * -----------------------------------------------------
 * Componente para enviar correo de reseteo de contraseña
 * -----------------------------------------------------
 * @version 0.0.1a
 * @created 2025-01-15
 * @modified 2025-08-29
 * @author Correcta
 * @author Leonard Vasilescu
 * @author Sergio Manjón
 * @copyright © Correcta
 * @license Private
 * */

"use client";

import React, { useState } from "react";
import InputComponent from "@/components/ui/InputComponent";
import FormComponent from "@/components/ui/FormComponent";
import { ResetPasswordSchema } from "@/schemas/ResetPasswordSchema";
import { sendResetMail } from "@/actions/common/user-actions";
import { useTranslations } from "use-intl";
import { HTTPStatus } from "@/constants/HttpStatus";
import SendResetEmailModal from "@/components/modals/auth/SendResetEmailModal";
import { ResetMailFormData } from "@/types/common/forms";
import { BUTTON_LABELS } from "@/constants/buttonLabels";

const SendResetMailPassword = () => {
  const placeholders = useTranslations("Placeholders");
  const labels = useTranslations("Labels");
  const invMail = useTranslations("Validation");
  const [formData, setFormData] = useState<ResetMailFormData>({
    mail: "",
  });
  const [errors, setErrors] = useState<{ [key: string]: string[] }>({});
  const [isOpenModalSendEmail, setIsOpenModalSendEmail] =
    useState<boolean>(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;

    setFormData({
      ...formData,
      [id]: value,
    });

    if (errors[id]) {
      setErrors((prevErrors) => {
        const newErrors = { ...prevErrors };
        delete newErrors[id];
        return newErrors;
      });
    }
  };

  const handleSubmit = async (formDataObj: FormData) => {
    const data = {
      mail: formDataObj.get("mail") as string,
    };

    const validationResult = ResetPasswordSchema.safeParse(data);

    if (validationResult.success) {
      setErrors({});
      const response = await sendResetMail(data);

      if (response.status === HTTPStatus.CREATED) {
        setIsOpenModalSendEmail(true);
      } else if (response.error) {
        setErrors({
          mail: [response.message || ""],
        });
      }
    } else {
      const formattedErrors = validationResult.error.flatten().fieldErrors;
      setErrors({
        mail: [invMail(formattedErrors.mail?.[0] ?? "mailInvalid")],
      });
    }
  };

  return (
    <>
      <FormComponent<ResetMailFormData>
        formData={formData}
        formAction={handleSubmit}
        useNativeForm={true}
        formClassName={`form ${Object.keys(errors).length > 0 ? "form--errors" : ""}`}
        buttonSubmit={true}
        buttonSubmitText={BUTTON_LABELS.SEND}
        buttonClassName="btn--full"
      >
        <InputComponent
          id="mail"
          name="mail"
          label={labels("labelMailRequired")}
          type="email"
          placeholder={placeholders("placeholderMail")}
          value={formData.mail}
          onChange={handleChange}
          noTranslation
          error={errors.mail && errors.mail[0]}
          autoComplete="email"
        />
        <span>{labels("labelRequired")}</span>
      </FormComponent>

      {isOpenModalSendEmail && (
        <SendResetEmailModal
          isOpen={isOpenModalSendEmail}
          onClose={() => setIsOpenModalSendEmail(!isOpenModalSendEmail)}
        />
      )}
    </>
  );
};

export default SendResetMailPassword;
